import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import api from '../../../utils/authUtils';
import { Plus, Edit, Trash2, X } from 'lucide-react';
import { format } from 'date-fns';

const Container = styled.div`
  margin-bottom: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: bold;
`;

const Button = styled.button`
  background-color: #3b82f6;
  color: white;
  font-weight: bold;
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  cursor: pointer;

  &:hover {
    background-color: #2563eb;
  }

  svg {
    margin-right: 0.5rem;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  background-color: #f3f4f6;
  border: 1px solid #e5e7eb;
  padding: 0.5rem;
  text-align: left;
`;

const Td = styled.td`
  border: 1px solid #e5e7eb;
  padding: 0.5rem;
`;

const ActionButton = styled.button`
  background-color: transparent;
  border: none;
  cursor: pointer;
  margin-right: 0.5rem;

  &:hover {
    color: #3b82f6;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 2rem;
  border-radius: 0.5rem;
  width: 80%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: bold;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  padding: 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.25rem;
`;

const TextArea = styled.textarea`
  padding: 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.25rem;
  resize: vertical;
`;

const BulletPointContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const BulletPointInput = styled(Input)`
  flex-grow: 1;
  margin-right: 0.5rem;
`;

const RemoveButton = styled(Button)`
  background-color: #ef4444;
  padding: 0.25rem 0.5rem;

  &:hover {
    background-color: #dc2626;
  }
`;

const Checkbox = styled.input`
  margin-right: 0.5rem;
`;

const LanguageColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;
`;

const LanguageHeader = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
`;

const ConferenceManagement = ({ onUpdate }) => {
  const [conferences, setConferences] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [currentConference, setCurrentConference] = useState(null);
  const [formData, setFormData] = useState({
    conference: {
      bigHeadline: '',
      subHeadline: '',
      mainHeadline: '',
      subText: '',
      dateTime: '',
      location: '',
      programText: '',
      isActive: false,
      isMain: false,
      bulletPointsHeadline: {
        headline: '',
        headlineEN: '',
        bulletPoints: [''],
        bulletPointsEN: ['']
      },
      textHeadline: {
        headline: '',
        headlineEN: '',
        textContent: '',
        textContentEN: ''
      },
      bigHeadlineEN: '',
      subHeadlineEN: '',
      mainHeadlineEN: '',
      subTextEN: '',
      programTextEN: ''
    },
    image: null,
    program: null
  });

  useEffect(() => {
    fetchConferences();
  }, []);

  const fetchConferences = async () => {
    try {
      const response = await api.get('/api/cms/conference');
      const processedConferences = response.data.map(conf => ({
        ...conf,
        isActive: Boolean(conf.isActive),
        isMain: Boolean(conf.isMain)
      }));
      console.log("conferences fetched: ", processedConferences)
      setConferences(processedConferences);
    } catch (error) {
      console.error('Error fetching conferences:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      conference: {
        ...prev.conference,
        [name]: type === 'checkbox' ? Boolean(checked) : value
      }
    }));
  };

  const handleNestedInputChange = (e, nestedField) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      conference: {
        ...prev.conference,
        [nestedField]: {
          ...prev.conference[nestedField],
          [name]: value
        }
      }
    }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: files[0]
    }));
  };

  const handleBulletPointChange = (index, value, field) => {
    setFormData(prev => ({
      ...prev,
      conference: {
        ...prev.conference,
        bulletPointsHeadline: {
          ...prev.conference.bulletPointsHeadline,
          [field]: prev.conference.bulletPointsHeadline[field].map((point, i) => 
            i === index ? value : point
          )
        }
      }
    }));
  };

  const addBulletPoint = (field) => {
    setFormData(prev => ({
      ...prev,
      conference: {
        ...prev.conference,
        bulletPointsHeadline: {
          ...prev.conference.bulletPointsHeadline,
          [field]: [...prev.conference.bulletPointsHeadline[field], '']
        }
      }
    }));
  };

  const removeBulletPoint = (index, field) => {
    setFormData(prev => ({
      ...prev,
      conference: {
        ...prev.conference,
        bulletPointsHeadline: {
          ...prev.conference.bulletPointsHeadline,
          [field]: prev.conference.bulletPointsHeadline[field].filter((_, i) => i !== index)
        }
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData();
    const conferenceData = {
      ...formData.conference,
      isActive: Boolean(formData.conference.isActive),
      isMain: Boolean(formData.conference.isMain)

    };
    console.log("conferenceData: ", conferenceData)
    data.append('conference', JSON.stringify(conferenceData));
    if (formData.image) data.append('image', formData.image);
    if (formData.program) data.append('program', formData.program);

    try {
      if (currentConference) {
        await api.put(`/api/cms/conference/${currentConference.id}`, data);
      } else {
        await api.post('/api/cms/conference', data);
      }
      setShowModal(false);
      setCurrentConference(null);
      fetchConferences();
      onUpdate();
    } catch (error) {
      console.error('Error saving conference:', error);
    }
  };


  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this conference?')) {
      try {
        await api.delete(`/api/cms/conference/${id}`);
        fetchConferences();
      } catch (error) {
        console.error('Error deleting conference:', error);
      }
    }
  };

  const openModal = (conference = null) => {
    if (conference) {
      setFormData({
        conference: {
          ...conference,
          dateTime: conference.dateTime ? format(new Date(conference.dateTime), "yyyy-MM-dd'T'HH:mm") : '',
          isActive: Boolean(conference.isActive),
          isMain: Boolean(conference.isMain),

          bulletPointsHeadline: {
            headline: conference.bulletPointsHeadline?.headline || '',
            headlineEN: conference.bulletPointsHeadline?.headlineEN || '',
            bulletPoints: conference.bulletPointsHeadline?.bulletPoints || [''],
            bulletPointsEN: conference.bulletPointsHeadline?.bulletPointsEN || ['']
          },
          textHeadline: {
            headline: conference.textHeadline?.headline || '',
            headlineEN: conference.textHeadline?.headlineEN || '',
            textContent: conference.textHeadline?.textContent || '',
            textContentEN: conference.textHeadline?.textContentEN || ''
          }
        },
        image: null,
        program: null
      });
      setCurrentConference(conference);
    } else {
      setFormData({
        conference: {
          bigHeadline: '',
          subHeadline: '',
          mainHeadline: '',
          subText: '',
          dateTime: '',
          location: '',
          programText: '',
          isActive: false,
          isMain: false,
          bulletPointsHeadline: {
            headline: '',
            headlineEN: '',
            bulletPoints: [''],
            bulletPointsEN: ['']
          },
          textHeadline: {
            headline: '',
            headlineEN: '',
            textContent: '',
            textContentEN: ''
          },
          bigHeadlineEN: '',
          subHeadlineEN: '',
          mainHeadlineEN: '',
          subTextEN: '',
          programTextEN: ''
        },
        image: null,
        program: null
      });
      setCurrentConference(null);
    }
    setShowModal(true);
  };

  return (
    <Container>
      <Header>
        <Title>Conference Management</Title>
        <Button onClick={() => openModal()}>
          <Plus size={20} />
          Create New Conference
        </Button>
      </Header>
      <Table>
        <thead>
          <tr>
            <Th>Big Headline (DE/EN)</Th>
            <Th>Sub Headline (DE/EN)</Th>
            <Th>Main Headline (DE/EN)</Th>
            <Th>Date</Th>
            <Th>Location</Th>
            <Th>Active</Th>
            <Th>Main Event</Th>
            <Th>Actions</Th>
          </tr>
        </thead>
        <tbody>
          {conferences.map((conference) => (
            <tr key={conference.id}>
              <Td>{conference.bigHeadline} / {conference.bigHeadlineEN}</Td>
              <Td>{conference.subHeadline} / {conference.subHeadlineEN}</Td>
              <Td>{conference.mainHeadline} / {conference.mainHeadlineEN}</Td>
              <Td>{format(new Date(conference.dateTime), 'PPP')}</Td>
              <Td>{conference.location}</Td>
              <Td>{conference.isActive ? 'Yes' : 'No'}</Td>
              <Td>{conference.isMain ? 'Yes' : 'No'}</Td>

              <Td>
                <ActionButton onClick={() => openModal(conference)}>
                  <Edit size={16} />
                </ActionButton>
                <ActionButton onClick={() => handleDelete(conference.id)}>
                  <Trash2 size={16} />
                </ActionButton>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>

      {showModal && (
        <Modal>
          <ModalContent>
            <h2>{currentConference ? 'Edit Conference' : 'Create New Conference'}</h2>
            <Form onSubmit={handleSubmit}>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <LanguageColumn>
                  <LanguageHeader>German Content</LanguageHeader>
                  <FormGroup>
                    <Label htmlFor="bigHeadline">Big Headline (DE)</Label>
                    <Input
                      type="text"
                      id="bigHeadline"
                      name="bigHeadline"
                      value={formData.conference.bigHeadline}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="subHeadline">Sub Headline (DE)</Label>
                    <Input
                      type="text"
                      id="subHeadline"
                      name="subHeadline"
                      value={formData.conference.subHeadline}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="mainHeadline">Main Headline (DE)</Label>
                    <Input
                      type="text"
                      id="mainHeadline"
                      name="mainHeadline"
                      value={formData.conference.mainHeadline}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="subText">Sub Text (DE)</Label>
                    <TextArea
                      id="subText"
                      name="subText"
                      value={formData.conference.subText}
                      onChange={handleInputChange}
                      rows={3}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="programText">Program Text (DE)</Label>
                    <TextArea
                      id="programText"
                      name="programText"
                      value={formData.conference.programText}
                      onChange={handleInputChange}
                      rows={4}
                    />
                  </FormGroup>
                </LanguageColumn>
                <LanguageColumn>
                  <LanguageHeader>English Content</LanguageHeader>
                  <FormGroup>
                    <Label htmlFor="bigHeadlineEN">Big Headline (EN)</Label>
                    <Input
                      type="text"
                      id="bigHeadlineEN"
                      name="bigHeadlineEN"
                      value={formData.conference.bigHeadlineEN}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="subHeadlineEN">Sub Headline (EN)</Label>
                    <Input
                      type="text"
                      id="subHeadlineEN"
                      name="subHeadlineEN"
                      value={formData.conference.subHeadlineEN}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="mainHeadlineEN">Main Headline (EN)</Label>
                    <Input
                      type="text"
                      id="mainHeadlineEN"
                      name="mainHeadlineEN"
                      value={formData.conference.mainHeadlineEN}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="subTextEN">Sub Text (EN)</Label>
                    <TextArea
                      id="subTextEN"
                      name="subTextEN"
                      value={formData.conference.subTextEN}
                      onChange={handleInputChange}
                      rows={3}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="programTextEN">Program Text (EN)</Label>
                    <TextArea
                      id="programTextEN"
                      name="programTextEN"
                      value={formData.conference.programTextEN}
                      onChange={handleInputChange}
                      rows={4}
                    />
                  </FormGroup>
                </LanguageColumn>
              </div>
              
              <FormGroup>
                <Label htmlFor="dateTime">Date and Time</Label>
                <Input
                  type="datetime-local"
                  id="dateTime"
                  name="dateTime"
                  value={formData.conference.dateTime}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="location">Location</Label>
                <Input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.conference.location}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="program">Program File</Label>
                <Input
                  type="file"
                  id="program"
                  name="program"
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx"
                />
              </FormGroup>
              <FormGroup>
                <Label>
                  <Checkbox
                    type="checkbox"
                    name="isActive"
                    checked={Boolean(formData.conference.isActive)}
                    onChange={handleInputChange}
                  />
                  Active
                </Label>
              </FormGroup>
              <FormGroup>
                <Label>
                  <Checkbox
                    type="checkbox"
                    name="isMain"
                    checked={Boolean(formData.conference.isMain)}
                    onChange={handleInputChange}
                  />
                  Main Event
                </Label>
              </FormGroup>
              <FormGroup>
                <Label htmlFor="image">Main Image</Label>
                <Input
                  type="file"
                  id="image"
                  name="image"
                  onChange={handleFileChange}
                  accept="image/*"
                />
              </FormGroup>
              
              <div style={{ display: 'flex', gap: '1rem' }}>
                <LanguageColumn>
                  <LanguageHeader>Bullet Points (DE)</LanguageHeader>
                  <FormGroup>
                    <Label htmlFor="bulletPointsHeadline">Bullet Points Headline (DE)</Label>
                    <Input
                      type="text"
                      id="bulletPointsHeadline"
                      name="headline"
                      value={formData.conference.bulletPointsHeadline.headline}
                      onChange={(e) => handleNestedInputChange(e, 'bulletPointsHeadline')}
                    />
                  </FormGroup>
                  {formData.conference.bulletPointsHeadline.bulletPoints.map((point, index) => (
                    <BulletPointContainer key={index}>
                      <BulletPointInput
                        type="text"
                        value={point}
                        onChange={(e) => handleBulletPointChange(index, e.target.value, 'bulletPoints')}
                      />
                      <RemoveButton type="button" onClick={() => removeBulletPoint(index, 'bulletPoints')}>
                        <X size={16} />
                      </RemoveButton>
                    </BulletPointContainer>
                  ))}
                  <Button type="button" onClick={() => addBulletPoint('bulletPoints')}>
                    Add Bullet Point (DE)
                  </Button>
                </LanguageColumn>
                <LanguageColumn>
                  <LanguageHeader>Bullet Points (EN)</LanguageHeader>
                  <FormGroup>
                    <Label htmlFor="bulletPointsHeadlineEN">Bullet Points Headline (EN)</Label>
                    <Input
                      type="text"
                      id="bulletPointsHeadlineEN"
                      name="headlineEN"
                      value={formData.conference.bulletPointsHeadline.headlineEN}
                      onChange={(e) => handleNestedInputChange(e, 'bulletPointsHeadline')}
                    />
                  </FormGroup>
                  {formData.conference.bulletPointsHeadline.bulletPointsEN.map((point, index) => (
                    <BulletPointContainer key={index}>
                      <BulletPointInput
                        type="text"
                        value={point}
                        onChange={(e) => handleBulletPointChange(index, e.target.value, 'bulletPointsEN')}
                      />
                      <RemoveButton type="button" onClick={() => removeBulletPoint(index, 'bulletPointsEN')}>
                        <X size={16} />
                      </RemoveButton>
                    </BulletPointContainer>
                  ))}
                  <Button type="button" onClick={() => addBulletPoint('bulletPointsEN')}>
                    Add Bullet Point (EN)
                  </Button>
                </LanguageColumn>
              </div>
              
              <div style={{ display: 'flex', gap: '1rem' }}>
                <LanguageColumn>
                  <LanguageHeader>Text Content (DE)</LanguageHeader>
                  <FormGroup>
                    <Label htmlFor="textHeadline">Text Headline (DE)</Label>
                    <Input
                      type="text"
                      id="textHeadline"
                      name="headline"
                      value={formData.conference.textHeadline.headline}
                      onChange={(e) => handleNestedInputChange(e, 'textHeadline')}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="textContent">Text Content (DE)</Label>
                    <TextArea
                      id="textContent"
                      name="textContent"
                      value={formData.conference.textHeadline.textContent}
                      onChange={(e) => handleNestedInputChange(e, 'textHeadline')}
                      rows={4}
                    />
                  </FormGroup>
                </LanguageColumn>
                <LanguageColumn>
                  <LanguageHeader>Text Content (EN)</LanguageHeader>
                  <FormGroup>
                    <Label htmlFor="textHeadlineEN">Text Headline (EN)</Label>
                    <Input
                      type="text"
                      id="textHeadlineEN"
                      name="headlineEN"
                      value={formData.conference.textHeadline.headlineEN}
                      onChange={(e) => handleNestedInputChange(e, 'textHeadline')}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="textContentEN">Text Content (EN)</Label>
                    <TextArea
                      id="textContentEN"
                      name="textContentEN"
                      value={formData.conference.textHeadline.textContentEN}
                      onChange={(e) => handleNestedInputChange(e, 'textHeadline')}
                      rows={4}
                    />
                  </FormGroup>
                </LanguageColumn>
              </div>
              <Button type="submit">Save</Button>
              <Button type="button" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
            </Form>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default ConferenceManagement;
